const VdisainToast = (() => {
    const TIMEOUT = 4000;
    const GAP = 12;
    const DISMISS_MS = 200;
    const MAX_VISIBLE = 3;

    const ICON = {
        success: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>',
        error: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>',
        warning: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>',
        info: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>',
        close: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
    };

    let container = null;
    let list = null;
    let toasts = [];
    let nextId = 0;
    let expanded = false;
    let collapseTimer = null;

    let timer = null;
    let timerStart = 0;
    let timerRemaining = TIMEOUT;

    const visibleToasts = () => toasts.filter(t => t.el.dataset.state !== 'dismissing');

    const el = (tag, attrs = {}, html = '') => {
        const node = document.createElement(tag);
        for (const [k, v] of Object.entries(attrs)) {
            if (k === 'className') node.className = v;
            else if (k.startsWith('data')) node.setAttribute(`data-${k.slice(4).replace(/[A-Z]/g, m => '-' + m.toLowerCase())}`, v);
            else node.setAttribute(k, v);
        }
        if (html) node.innerHTML = html;
        return node;
    };

    const ensureContainer = () => {
        if (container) return;

        container = el('section', { className: 'vdisain-toaster', role: 'region', 'aria-label': 'Notifications' });
        list = el('ol', { className: 'vdisain-toaster__list' });
        container.append(list);
        document.body.append(container);

        container.addEventListener('mouseover', ({ target, relatedTarget }) => {
            const toast = target.closest('.vdisain-toast');
            if (!toast || toast.contains(relatedTarget)) return;
            clearTimeout(collapseTimer);
            if (!expanded) expand();
        });

        container.addEventListener('mouseout', ({ target, relatedTarget }) => {
            const toast = target.closest('.vdisain-toast');
            if (!toast || toast.contains(relatedTarget)) return;
            collapseTimer = setTimeout(() => { if (expanded) collapse(); }, 200);
        });

        container.addEventListener('click', (e) => {
            const btn = e.target.closest('.vdisain-toast__close');
            if (!btn) return;
            e.stopPropagation();
            const id = Number(btn.closest('.vdisain-toast').dataset.toastId);
            dismiss(id);
        });
    };

    const show = (message, type = 'info') => {
        ensureContainer();

        const id = ++nextId;
        const icon = ICON[type] ?? ICON.info;

        const toast = el('li', { className: 'vdisain-toast', 'data-toast-id': id, 'data-type': type, 'data-state': 'entering' }, `
            <div class="vdisain-toast__icon" aria-hidden="true">${icon}</div>
            <div class="vdisain-toast__content">${message}</div>
            <button class="vdisain-toast__close" type="button" aria-label="Dismiss notification">${ICON.close}</button>
        `);

        list.append(toast);
        toasts.push({ id, el: toast });
        updateStack();

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                toast.dataset.state = 'visible';
                if (!timer) startFrontTimer();
            });
        });

        return id;
    };

    // --- Dismiss ---
    const dismiss = (id) => {
        const record = toasts.find(t => t.id === id);
        if (!record) return;

        const front = visibleToasts()[0];
        if (front?.id === id) stopTimer();

        record.el.dataset.state = 'dismissing';

        setTimeout(() => {
            record.el.remove();
            toasts = toasts.filter(t => t.id !== id);
            updateStack();

            if (!toasts.length && container) {
                delete container.dataset.expanded;
                expanded = false;
            }

            // Next front toast gets a fresh timer
            startFrontTimer();
        }, DISMISS_MS);
    };

    const updateStack = () => {
        const visible = visibleToasts();
        let offset = 0;

        for (let i = 0; i < visible.length; i++) {
            const { el } = visible[i];
            const h = el.offsetHeight || 0;

            el.style.setProperty('--index', i);
            el.style.setProperty('--offset', offset);
            el.style.setProperty('--count', visible.length);

            if (i >= MAX_VISIBLE) el.dataset.beyond = '';
            else delete el.dataset.beyond;

            offset += h + GAP;
        }
    };

    const startFrontTimer = () => {
        stopTimer();
        if (expanded) return;

        const front = visibleToasts()[0];
        if (!front) return;

        timerRemaining = TIMEOUT;
        timerStart = Date.now();
        timer = setTimeout(() => {
            timer = null;
            dismiss(front.id);
        }, TIMEOUT);
    };

    const pauseTimer = () => {
        if (!timer) return;
        clearTimeout(timer);
        timerRemaining = Math.max(timerRemaining - (Date.now() - timerStart), 0);
        timer = null;
    };

    const resumeTimer = () => {
        if (timer || expanded) return;

        const front = visibleToasts()[0];
        if (!front || timerRemaining <= 0) return;

        timerStart = Date.now();
        timer = setTimeout(() => {
            timer = null;
            dismiss(front.id);
        }, timerRemaining);
    };

    const stopTimer = () => {
        if (timer) { clearTimeout(timer); timer = null; }
        timerRemaining = TIMEOUT;
    };

    const expand = () => {
        expanded = true;
        container?.setAttribute('data-expanded', '');
        updateStack();
        pauseTimer();
    };

    const collapse = () => {
        expanded = false;
        container?.removeAttribute('data-expanded');
        resumeTimer();
    };

    return {
        show,
        success: (msg) => show(msg, 'success'),
        error: (msg) => show(msg, 'error'),
        warning: (msg) => show(msg, 'warning'),
        info: (msg) => show(msg, 'info'),
        dismiss,
    };
})();

window.VdisainToast = VdisainToast;

document.addEventListener('DOMContentLoaded', () => {

    // --- Capture WordPress login errors & notices ---
    const loginError = document.getElementById('login_error');
    if (loginError) VdisainToast.error(loginError.innerHTML);

    document.querySelectorAll('.message, .notice, .login-action-login .message').forEach((msg) => {
        if (msg.id === 'login_error') return;

        let type = 'success';
        if (msg.classList.contains('notice-error') || msg.classList.contains('error')) type = 'error';
        else if (msg.classList.contains('notice-warning')) type = 'warning';
        else if (msg.classList.contains('notice-info')) type = 'info';

        VdisainToast.show(msg.innerHTML, type);
    });

    const announce = (message) => {
        const node = document.createElement('div');
        node.setAttribute('role', 'status');
        node.setAttribute('aria-live', 'polite');
        node.className = 'screen-reader-only';
        node.textContent = message;
        document.body.append(node);
        setTimeout(() => node.remove(), 1000);
    };

    const toggleBtn = document.getElementById('theme-toggle');

    if (toggleBtn) {
        const SUN = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-sun-icon lucide-sun"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>';
        const MOON = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-sun-moon-icon lucide-sun-moon"><path d="M12 2v2"/><path d="M14.837 16.385a6 6 0 1 1-7.223-7.222c.624-.147.97.66.715 1.248a4 4 0 0 0 5.26 5.259c.589-.255 1.396.09 1.248.715"/><path d="M16 12a4 4 0 0 0-4-4"/><path d="m19 5-1.256 1.256"/><path d="M20 12h2"/></svg>';

        const applyTheme = (light) => {
            document.documentElement.style.colorScheme = light ? 'light' : 'dark';
            document.documentElement.dataset.theme = light ? 'light' : 'dark';
            toggleBtn.innerHTML = light ? MOON : SUN;
        };

        const saved = localStorage.getItem('vdisain-theme');
        applyTheme(saved ? saved === 'light' : !matchMedia('(prefers-color-scheme: dark)').matches);

        toggleBtn.addEventListener('click', () => {
            const light = document.documentElement.dataset.theme !== 'light';
            applyTheme(light);
            localStorage.setItem('vdisain-theme', light ? 'light' : 'dark');
            announce(`Theme changed to ${light ? 'light' : 'dark'} mode`);
        });
    }

    const loginForm = document.getElementById('loginform');
    const rememberMe = document.querySelector('.forgetmenot');
    const lostPwd = document.getElementById('nav');
    const submitBtn = document.querySelector('.submit');

    if (loginForm && rememberMe && lostPwd && submitBtn && !document.getElementById('btn-show-forgot')) {
        const row = document.createElement('div');
        row.className = 'vdisain-login-options';
        row.append(rememberMe);

        const wrap = document.createElement('div');
        wrap.className = 'vdisain-lost-link';

        const link = document.createElement('button');
        link.type = 'button';
        link.id = 'btn-show-forgot';
        link.textContent = globalThis.vdisainLocal?.lostPassword ?? 'Parool kadunud?';

        wrap.append(link);
        row.append(wrap);
        loginForm.insertBefore(row, submitBtn);

        lostPwd.style.display = 'none';
        document.getElementById('backtoblog')?.style.setProperty('display', 'none');
    }

    const adminEmailForm = document.querySelector('.admin-email-confirm-form');
    if (adminEmailForm) {
        const secondary = adminEmailForm.querySelector('.admin-email__actions-secondary');
        const backtoblog = document.getElementById('backtoblog');
        if (secondary && backtoblog) {
            secondary.appendChild(backtoblog);
        }
    }

    const viewLogin = document.getElementById('view-login');
    const viewForgot = document.getElementById('view-forgot');
    const headerLogin = document.getElementById('header-login');
    const headerForgot = document.getElementById('header-forgot');

    const switchView = (showForgot) => {
        if (!viewLogin || !viewForgot) return;

        viewLogin.classList.toggle('is-active', !showForgot);
        viewForgot.classList.toggle('is-active', showForgot);
        headerLogin?.classList.toggle('is-hidden', showForgot);
        headerForgot?.classList.toggle('is-hidden', !showForgot);

        const target = showForgot ? viewForgot : viewLogin;
        target.querySelector('input[type="text"]')?.focus();
        announce(showForgot ? 'Showing password recovery form' : 'Showing login form');
    };

    document.getElementById('btn-show-forgot')?.addEventListener('click', (e) => {
        e.preventDefault();
        switchView(true);
    });

    document.getElementById('btn-back-login')?.addEventListener('click', (e) => {
        e.preventDefault();
        switchView(false);
    });

    const langToggle = document.getElementById('lang-toggle');
    const langMenu = document.getElementById('lang-menu');

    if (langToggle && langMenu) {
        const close = () => {
            langMenu.classList.remove('is-open');
            langToggle.setAttribute('aria-expanded', 'false');
        };

        const open = () => {
            langMenu.classList.add('is-open');
            langToggle.setAttribute('aria-expanded', 'true');
            const focus = langMenu.querySelector('.vdisain-lang-item.is-active') ?? langMenu.querySelector('.vdisain-lang-item');
            focus?.focus();
        };

        langToggle.addEventListener('click', (e) => {
            e.stopPropagation();
            langMenu.classList.contains('is-open') ? close() : open();
        });

        document.addEventListener('click', (e) => {
            if (!langMenu.contains(e.target) && !langToggle.contains(e.target)) close();
        });

        document.addEventListener('keydown', (e) => {
            if (!langMenu.classList.contains('is-open')) return;

            if (e.key === 'Escape') {
                close();
                langToggle.focus();
                return;
            }

            if (e.key !== 'Tab') return;

            const items = [...langMenu.querySelectorAll('.vdisain-lang-item')];
            const first = items[0];
            const last = items.at(-1);

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last?.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first?.focus();
            }
        });
    }
});
